"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import {
  insertCategory,
  updateCategory,
  findAllCategory,
  findByIDCategory,
} from "./category.service";
import { categoryValidationSchema } from "./category.validation";
import { clearCategoryDetails } from "./category.slice";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const CategoryForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { CategoryDetail, formLoading } = useAppSelector(
    (state) => state.category,
  );
  const { data: CategoryID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    if (CategoryID) {
      dispatch(findByIDCategory(CategoryID));
    }
  }, [CategoryID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearCategoryDetails());
      // Note: We don't call closed() here because the drawer manager handles it.
      // But we clear details so the next open starts fresh.
    };
  }, [dispatch]);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      ID: CategoryID || 0,
      IsActive: String(values.IsActive) === "true",
    };

    if (CategoryID) {
      await dispatch(updateCategory(payload));
    } else {
      await dispatch(insertCategory(payload));
    }

    dispatch(findAllCategory());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading} />
      <Formik
        initialValues={{
          Title: CategoryDetail.Title || "",
          IsActive: CategoryDetail.IsActive ?? true,
        }}
        validationSchema={categoryValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-4">
              <InputField
                label="Category Title"
                name="Title"
                placeholder="Enter category title"
                required
              />

              <SelectField
                label="Status"
                name="IsActive"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {CategoryID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CategoryForm;
